import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { catchError, exhaustMap, map, tap, mergeMap } from "rxjs/operators";

import * as UsersActionTypes from "../../actions/users/user.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { UsersService } from "src/app/shared/services/users/user.service";
import { LocalesService } from "src/app/shared/services/locales/locales.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class UsersEffects {
  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getUsers),
      exhaustMap((action) =>
        this.usersService
          .getUsers(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) => UsersActionTypes.loadUsers({ response: result })),
            catchError((error) => {
              const actions = [
                UsersActionTypes.loadUsers({ response: { result: [] } as any }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "UserManagement.errorLoadUsers",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getUsersExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getUsersExcel),
      exhaustMap((action) =>
        this.usersService
          .getUsers(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              UsersActionTypes.loadUsersExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                UsersActionTypes.loadUsersExcel({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "UserManagement.errorLoadUserExcel",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Users Table Config
  getUsersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getUsersTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(
            action.options.code,
            action.options.settingType
          )
          .pipe(
            map((result) =>
            UsersActionTypes.loadUsersTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                UsersActionTypes.loadUsersTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Users Table Config
  saveUsersTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.saveUsersTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveSuccess",
                  SnackbarType.SUCCESS
                )
              )
            ]
          ),
          catchError((error: any) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.updateUser),
      exhaustMap((action) =>
        this.usersService.updateUser(action).pipe(
          map((result) => {
            const { result: { username, name }} = result;
            if (localStorage.getItem("username") === username) {
              localStorage.setItem("loggedUser", name);
            }
            return UsersActionTypes.getUser({ user: username });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.UserUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "UserManagement.UserUpdateFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getUser),
      exhaustMap((action) =>
        this.usersService.getUser(action).pipe(
          map((result) => UsersActionTypes.setUser({ user: result })),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorLoadUser",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActionTypes.setLoggedUser),
        tap((payload) => {
          localStorage.setItem("loggedUser", (payload as any).user.result.name);
        })
      ),
    { dispatch: false }
  );

  getUserProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getUserProfiles),
      exhaustMap(() =>
        this.usersService.getUserProfiles().pipe(
          map((result) =>
            UsersActionTypes.setUserProfiles({ userProfiles: result })
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorLoadUserProfiles",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.resetUserPassword),
      exhaustMap((action) =>
        this.usersService.resetUserPassword(action).pipe(
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Auth.PasswordRecoverySuccess",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Auth.PasswordRecoveryError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.changePassword),
      exhaustMap((action) =>
        this.usersService.changePassword(action.passwords).pipe(
          mergeMap(() => [
            UsersActionTypes.loadUsersLoadEnd(),
            UsersActionTypes.changePasswordSuccess(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "UserManagement.PasswordChange",
                SnackbarType.SUCCESS
              )
            )
          ]),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.PasswordChangeFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLocales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getLocales),
      exhaustMap((action) =>
        this.localesService.getLocales(action.options).pipe(
          map((result) => UsersActionTypes.loadLocales({ response: result })),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorGetLocales",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.changeStatus),
      exhaustMap((action) =>
        this.usersService.changeStatus(action).pipe(
          mergeMap((result) => {
            return [
              UsersActionTypes.getUsers(null),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.StatusChange",
                  SnackbarType.SUCCESS
                )
              ),
            ];
          }),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorChangingStatus",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.createUser),
      exhaustMap(({ user }) =>
        this.usersService.createUser(user).pipe(
         mergeMap((result) => [
            UsersActionTypes.resetCreation(),
            UsersActionTypes.getUser({ user: result?.result?.username })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.UserCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.UserCreateFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLoggedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.getLoggedUser),
      exhaustMap((action) =>
        this.usersService.getUser(action).pipe(
          map((result) => {
            localStorage.setItem("profileCode", result.result?.profile?.code);
            localStorage.setItem("entity", result.result?.entity);
            localStorage.setItem("entityToken", result.result?.entityToken);
            this.store$.dispatch(UsersActionTypes.getUserProfiles());
            return UsersActionTypes.setLoggedUser({ user: result });
          }),
          catchError((error) => {
            const actions = [
              UsersActionTypes.loadUsersLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.errorLoggedUser",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  setDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.setDisabled),
      exhaustMap((action) =>
        this.usersService.changeStatus(action).pipe(
          map((result) =>
            UsersActionTypes.getUsers({
              options: {
                paging: {
                  skip: 0,
                  top: 99999, // this.perPage
                },
                filteringArgs: undefined, // filteringExpr,
                sortingArgs: undefined, // sortingExpr
              },
            })
          ),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.setDisabled",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.getUsers({
                options: {
                  paging: {
                    skip: 0,
                    top: 99999, // this.perPage
                  },
                  filteringArgs: undefined, // filteringExpr,
                  sortingArgs: undefined, // sortingExpr
                },
              }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.userDisableFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  setEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActionTypes.setEnabled),
      exhaustMap((action) =>
        this.usersService.changeStatus(action).pipe(
          map((result) =>
            UsersActionTypes.getUsers({
              options: {
                paging: {
                  skip: 0,
                  top: 99999, // this.perPage
                },
                filteringArgs: undefined, // filteringExpr,
                sortingArgs: undefined, // sortingExpr
              },
            })
          ),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.setEnabled",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              UsersActionTypes.getUsers({
                options: {
                  paging: {
                    skip: 0,
                    top: 99999, // this.perPage
                  },
                  filteringArgs: undefined, // filteringExpr,
                  sortingArgs: undefined, // sortingExpr
                },
              }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "UserManagement.userEnableFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private usersService: UsersService,
    private router: Router,
    private localesService: LocalesService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
